<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  getImportSessionRows,
  updateImportSession,
  getAreas,
  validateImportSession
} from '@/api/api.js'
import { useStore } from '@/stores/store'
import MdiChevronRight from '~icons/mdi/chevron-right'
import MdiChevronLeft from '~icons/mdi/chevron-left'
import MdiAlertCircle from '~icons/mdi/alert-circle'
import MdiChevronDown from '~icons/mdi/chevron-down'
import BaseFormField from '@/components/base/BaseFormField.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
const props = defineProps({
  session: { type: Object, required: true },
  columnMapping: { type: Object, required: true },
  defaults: { type: Object, default: () => ({}) },
  categoryHierarchy: { type: Object, default: () => ({}) }
})
const emit = defineEmits(['advance'])

const store = useStore()
const { t } = useI18n()

const PAGE_SIZE = 50
const page = ref(1)
const totalPages = ref(1)
const totalCount = ref(0)
const columns = ref([])
const allRows = ref([]) // complete working set (loaded progressively)
const groupEdits = ref({})
const groupAliases = ref({})
const loadedPages = ref(new Set())
const loading = ref(false)
const saving = ref(false)
const saveError = ref('')
const loadError = ref('')
const repairRequired = ref(false)
const serverErrors = ref([])

// item_type overrides per item name (from session or user edits)
const itemTypeOverrides = ref({ ...(props.session.item_type_overrides || {}) })

// Suggested types from server
const suggestedItemTypes = ref(props.session.suggested_item_types || {})
const existingItemTypes = ref({})

// Collapsed item groups
const collapsedGroups = ref(new Set())

// Asset tags that already exist in the DB
const conflictingTags = ref(new Set())

const areas = ref([])
const categories = ref([])

const STATUS_OPTIONS = computed(() => [
  { value: '1', label: t('instanceStatus.1') },
  { value: '2', label: t('instanceStatus.2') },
  { value: '3', label: t('instanceStatus.3') }
])

const ITEM_TYPE_OPTIONS = computed(() => [
  { value: 1, label: t('itemTypes.1') },
  { value: 2, label: t('itemTypes.multiItem') },
  { value: 3, label: t('itemTypes.3') },
  { value: 4, label: t('itemTypes.4') }
])

onMounted(async () => {
  // Load reference data for dropdowns
  try {
    const [areaRes] = await Promise.all([getAreas({ page_size: 500 })])
    areas.value = areaRes.results || (Array.isArray(areaRes) ? areaRes : [])
    if (store.categories.length === 0) await store.fetchCategories()
    categories.value = store.categories
  } catch {
    /* non-fatal */
  }

  await loadPage(1)
  await refreshValidation()
})

async function refreshValidation() {
  try {
    const res = await validateImportSession(props.session.id)
    serverErrors.value = res.errors || []
    existingItemTypes.value = res.existing_item_types || {}
    conflictingTags.value = new Set(res.conflicting_asset_tags || [])
    loadError.value = ''
    return true
  } catch (err) {
    if (repairRequired.value && err?.status === 409) return true
    loadError.value = err?.data?.error || t('import.previewValidationError')
    return false
  }
}

async function loadPage(p, { silent = false } = {}) {
  if (loadedPages.value.has(p)) {
    if (!silent) page.value = p
    return true
  }
  if (!silent) loading.value = true
  try {
    const res = await getImportSessionRows(props.session.id, p, PAGE_SIZE)
    const data = res
    repairRequired.value = !!data.repair_required
    totalPages.value = data.num_pages || 1
    totalCount.value = data.count || 0
    if (columns.value.length === 0) columns.value = data.columns || []
    if (data.suggested_item_types) suggestedItemTypes.value = data.suggested_item_types

    // Apply mapping to raw rows if not already mapped
    const rows = (data.results || []).map((raw, i) => {
      const globalIdx = (p - 1) * PAGE_SIZE + i
      // Rows from backend are already applied with mapping if edited_rows exist;
      // otherwise they are raw rows — we apply mapping client-side display
      const row = normaliseRow(raw, globalIdx, data.is_mapped)
      const sourceName = (row.name || '').trim() || t('ui.noName')
      return { ...row, ...(groupEdits.value[sourceName] || {}) }
    })

    // Merge into allRows at correct offsets
    const offset = (p - 1) * PAGE_SIZE
    if (allRows.value.length < offset + rows.length) {
      allRows.value.length = offset + rows.length
    }
    rows.forEach((r, i) => {
      allRows.value[offset + i] = r
    })
    loadedPages.value.add(p)
    if (!silent) page.value = p
    loadError.value = ''
    return true
  } catch (err) {
    loadError.value = err?.data?.error || t('import.previewRowsError')
    return false
  } finally {
    if (!silent) loading.value = false
  }
}

async function retryLoad() {
  if (!loadedPages.value.has(page.value) && !await loadPage(page.value)) return
  await refreshValidation()
}

function normaliseRow(raw, globalIdx, isMapped) {
  // If the row is already field-keyed (edited_rows), return as-is with __idx
  if (isMapped) {
    return { ...raw, __idx: globalIdx }
  }
  // Apply column mapping client-side
  const cm = props.columnMapping || {}
  const defs = props.defaults || {}
  const mapped = { __idx: globalIdx }
  const fieldKeys = [
    'name',
    'description',
    'category',
    'rental_price_per_day',
    'weight_kg',
    'volume_m3',
    'is_container',
    'available_in_portal',
    'asset_tag',
    'serial_number',
    'purchase_price',
    'purchase_date',
    'status',
    'comment',
    'area',
    'supplier',
    'available_quantity'
  ]
  fieldKeys.forEach((key) => {
    const col = cm[key]
    const rawVal = col ? (raw[col] ?? '') : ''
    mapped[key] = rawVal !== '' ? String(rawVal) : defs[key] !== undefined ? String(defs[key]) : ''
  })
  return mapped
}

// Current page rows
const pageRows = computed(() => {
  const offset = (page.value - 1) * PAGE_SIZE
  return allRows.value.slice(offset, offset + PAGE_SIZE).filter(Boolean)
})

// Group page rows by item name
const groupedRows = computed(() => {
  const groups = []
  const seen = new Map() // name -> group index
  pageRows.value.forEach((row) => {
    const name = (row.name || '').trim() || t('ui.noName')
    if (seen.has(name)) {
      groups[seen.get(name)].rows.push(row)
    } else {
      seen.set(name, groups.length)
      groups.push({ name, rows: [row] })
    }
  })
  return groups
})

// Inline validation
const rowErrors = computed(() => {
  const errs = {}
  serverErrors.value.forEach((error) => {
    if (error.row_index != null) {
      errs[error.row_index] ||= {}
      errs[error.row_index][error.field] = error.message
    }
  })
  const seenTags = new Map()
  allRows.value.filter(Boolean).forEach((row) => {
    const idx = row.__idx
    const fieldErrs = {}
    if (!row.name?.trim()) fieldErrs.name = t('common.required')
    const tag = row.asset_tag?.trim()
    if (tag) {
      if (seenTags.has(tag)) {
        fieldErrs.asset_tag = t('ui.duplicateRow', { row: seenTags.get(tag) + 1 })
      } else if (conflictingTags.value.has(tag)) {
        fieldErrs.asset_tag = t('ui.assetTagExists')
      } else {
        seenTags.set(tag, idx)
      }
    }
    if (Object.keys(fieldErrs).length) errs[idx] = { ...(errs[idx] || {}), ...fieldErrs }
  })
  return errs
})

const globalErrors = computed(() => serverErrors.value.filter((error) => error.row_index == null))
const totalErrors = computed(() => Object.keys(rowErrors.value).length + globalErrors.value.length)
const hasErrors = computed(() => totalErrors.value > 0)

function getEffectiveType(name) {
  return itemTypeOverrides.value[name] ?? existingItemTypes.value[name] ?? suggestedItemTypes.value[name] ?? 1
}

function setItemType(name, val) {
  itemTypeOverrides.value[name] = parseInt(val)
  serverErrors.value = []
}

function cellError(row, field) {
  return rowErrors.value[row.__idx]?.[field]
}

function rowErrorMessages(row) {
  return Object.entries(rowErrors.value[row.__idx] || {}).map(([field, message]) => `${field}: ${message}`)
}

function isUnlistedArea(value) {
  const current = String(value || '').trim()
  return !!current && !areas.value.some((area) => String(area.id) === current || area.name === current)
}

function updateCell(row, field, value) {
  const idx = row.__idx
  const rowInAll = allRows.value[idx]
  if (rowInAll) rowInAll[field] = value
  serverErrors.value = []
  // If user changed an asset tag, remove old value from conflict set so error clears
}

// Update an item-level field across ALL rows in a group (shared item template)
function updateGroupField(groupName, field, value) {
  serverErrors.value = []
  const sourceName = groupAliases.value[groupName] || groupName
  groupEdits.value[sourceName] = { ...(groupEdits.value[sourceName] || {}), [field]: value }
  if (field === 'name') {
    groupAliases.value[value] = sourceName
    if (itemTypeOverrides.value[groupName] !== undefined) {
      itemTypeOverrides.value[value] = itemTypeOverrides.value[groupName]
      delete itemTypeOverrides.value[groupName]
    }
  }
  const isNoName = groupName === t('ui.noName')
  allRows.value.filter(Boolean).forEach((r) => {
    const rowGroupName = (r.name || '').trim() || t('ui.noName')
    if (rowGroupName === groupName || (!isNoName && r.name === groupName)) {
      r[field] = value
    }
  })
}

function toggleGroup(name) {
  if (collapsedGroups.value.has(name)) {
    collapsedGroups.value.delete(name)
  } else {
    collapsedGroups.value.add(name)
  }
  // Force reactivity
  collapsedGroups.value = new Set(collapsedGroups.value)
}

function jumpToFirstError() {
  const indices = Object.keys(rowErrors.value).map(Number)
  if (!indices.length) return
  const firstErrIdx = Math.min(...indices)
  const targetPage = Math.floor(firstErrIdx / PAGE_SIZE) + 1
  if (targetPage !== page.value) loadPage(targetPage)
}

async function advance() {
  if (hasErrors.value || loadError.value) return
  saving.value = true
  saveError.value = ''
  try {
    // Load any pages the user hasn't scrolled to yet so we save all rows,
    // not just the first visible page.
    const missing = []
    for (let p = 1; p <= totalPages.value; p++) {
      if (!loadedPages.value.has(p)) missing.push(p)
    }
    if (missing.length > 0) {
      const loaded = await Promise.all(missing.map((p) => loadPage(p, { silent: true })))
      if (loaded.some((ok) => !ok)) throw new Error(t('import.previewAllRowsError'))
    }
    if (allRows.value.filter(Boolean).length !== totalCount.value) {
      throw new Error(t('import.previewMissingRows'))
    }
    if (hasErrors.value) return

    const cleanRows = allRows.value.filter(Boolean).map((r) => {
      const { __idx, ...rest } = r
      return rest
    })
    await updateImportSession(props.session.id, {
      item_type_overrides: itemTypeOverrides.value,
      edited_rows: cleanRows,
      status: 'reviewing'
    })
    repairRequired.value = false
    if (!await refreshValidation()) return
    if (hasErrors.value) return
    emit('advance', { item_type_overrides: itemTypeOverrides.value })
  } catch (err) {
    saveError.value = err?.data?.error || err?.message || t('import.previewSaveError')
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <div class="flex flex-col gap-4">
    <div v-if="repairRequired" class="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-(--color-text)" role="status">
      {{ $t('import.previewRepairRequired') }}
    </div>
    <div v-if="loadError" class="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-(--color-text)" role="alert">
      {{ loadError }}
      <button type="button" class="btn btn-ghost btn-small ml-2" @click="retryLoad">{{ $t('import.tryAgain') }}</button>
    </div>
    <ul v-if="globalErrors.length" class="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-400" role="alert">
      <li v-for="(issue, index) in globalErrors" :key="`${issue.field}-${index}`">{{ issue.message }}</li>
    </ul>
    <!-- Sticky header -->
    <div
      class="sticky top-0 z-10 flex items-center justify-between rounded-xl border border-(--color-border) bg-(--color-surface) px-4 py-3"
    >
      <div class="flex items-center gap-3">
        <span class="text-(--color-text) text-sm font-medium"> {{ totalCount }} {{ $t('ui.rows') }} </span>
        <span v-if="hasErrors" class="flex items-center gap-1.5 text-red-400 text-sm">
          <MdiAlertCircle class="w-4 h-4" />
          {{ $t('import.previewIssueCount', { count: totalErrors }) }}
          <button v-if="Object.keys(rowErrors).length" class="underline text-xs" @click="jumpToFirstError">{{ $t('ui.jumpToFirst') }}</button>
        </span>
        <span v-else class="text-(--color-accent) text-sm">{{ $t('import.previewNoIssues') }}</span>
      </div>

      <div class="flex items-center gap-2">
        <button
          class="btn btn-ghost btn-small"
          :disabled="page <= 1 || loading"
          @click="loadPage(page - 1)"
        >
          <MdiChevronLeft class="w-4 h-4" />
        </button>
        <span class="text-(--color-muted) text-sm">{{ $t('ui.page') }} {{ page }} / {{ totalPages }}</span>
        <button
          class="btn btn-ghost btn-small"
          :disabled="page >= totalPages || loading"
          @click="loadPage(page + 1)"
        >
          <MdiChevronRight class="w-4 h-4" />
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex justify-center py-12 text-(--color-muted)">
      <span
        class="inline-block w-6 h-6 rounded-full border-2 border-(--color-accent) border-t-transparent animate-spin"
      />
    </div>

    <!-- Spreadsheet -->
    <template v-else>
      <div
        v-for="group in groupedRows"
        :key="group.name"
        class="flex flex-col rounded-xl border border-(--color-border) overflow-hidden"
      >
        <!-- Group header -->
        <div
          class="flex items-center justify-between px-4 py-2 bg-(--color-surface-2) cursor-pointer"
          @click="toggleGroup(group.name)"
        >
          <div class="flex items-center gap-3">
            <component
              :is="collapsedGroups.has(group.name) ? MdiChevronRight : MdiChevronDown"
              class="w-4 h-4 text-(--color-muted)"
            />
            <span class="font-medium text-(--color-text)">{{ group.name }}</span>
            <span v-if="group.name in existingItemTypes" class="badge badge-muted text-xs">{{ $t('import.existingBadge') }}</span>
            <span class="badge badge-muted text-xs"
              >{{ group.rows.length }} {{ $t('ui.instance') }}{{ group.rows.length !== 1 ? 's' : '' }}</span
            >
          </div>

          <div class="flex items-center gap-2" @click.stop>
            <span class="text-(--color-muted) text-xs">{{ $t('ui.itemType') }}</span>
            <BaseSelect
              :model-value="getEffectiveType(group.name)"
              density="compact"
              :aria-label="$t('ui.itemType')"
              @update:model-value="setItemType(group.name, $event)"
            >
              <option v-for="opt in ITEM_TYPE_OPTIONS" :key="opt.value" :value="opt.value">
                {{ opt.label }}
              </option>
            </BaseSelect>
            <span
              v-if="!(group.name in itemTypeOverrides) && suggestedItemTypes[group.name]"
              class="badge badge-accent text-xs"
              >{{ $t('ui.autoDetected') }}</span
            >
          </div>
        </div>

        <!-- Item-level details (shared across all instances in group) -->
        <template v-if="!collapsedGroups.has(group.name)">
          <div class="border-t border-(--color-border) bg-(--color-surface-2)/50 px-4 py-3">
            <p class="text-(--color-muted) text-xs font-medium uppercase tracking-wide mb-2">
              {{ $t('ui.itemDetailsShared') }}
            </p>
            <div class="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3 lg:grid-cols-4">
              <!-- Name -->
              <BaseFormField
                :label="$t('import.fieldItemName')"
                required
                density="compact"
                :error="!group.rows[0]?.name?.trim() ? $t('common.required') : ''"
              >
                <BaseInput
                  :model-value="group.rows[0]?.name"
                  density="compact"
                  @update:model-value="updateGroupField(group.name, 'name', $event)"
                />
              </BaseFormField>

              <!-- Category -->
              <BaseFormField :label="$t('entity.category')" density="compact">
                <BaseInput
                  :model-value="group.rows[0]?.category"
                  density="compact"
                  @update:model-value="updateGroupField(group.name, 'category', $event)"
                />
              </BaseFormField>

              <!-- Description -->
              <BaseFormField
                class="col-span-2 sm:col-span-3 lg:col-span-2"
                :label="$t('common.description')"
                density="compact"
              >
                <BaseInput
                  :model-value="group.rows[0]?.description"
                  density="compact"
                  @update:model-value="updateGroupField(group.name, 'description', $event)"
                />
              </BaseFormField>

              <!-- Rental Price / Day -->
              <BaseFormField :label="$t('itemView.rentalPrice')" density="compact">
                <BaseInput
                  :model-value="group.rows[0]?.rental_price_per_day"
                  type="number"
                  step="0.01"
                  min="0"
                  density="compact"
                  @update:model-value="updateGroupField(group.name, 'rental_price_per_day', $event)"
                />
              </BaseFormField>

              <!-- Weight -->
              <BaseFormField :label="$t('import.fieldWeight')" density="compact">
                <BaseInput
                  :model-value="group.rows[0]?.weight_kg"
                  type="number"
                  step="0.01"
                  min="0"
                  density="compact"
                  @update:model-value="updateGroupField(group.name, 'weight_kg', $event)"
                />
              </BaseFormField>

              <!-- Volume -->
              <BaseFormField :label="$t('import.fieldVolume')" density="compact">
                <BaseInput
                  :model-value="group.rows[0]?.volume_m3"
                  type="number"
                  step="0.001"
                  min="0"
                  density="compact"
                  @update:model-value="updateGroupField(group.name, 'volume_m3', $event)"
                />
              </BaseFormField>

              <!-- Booleans -->
              <div class="flex flex-col gap-1.5 justify-end">
                <button
                  type="button"
                  class="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors cursor-pointer w-full"
                  :class="
                    group.rows[0]?.is_container === 'true' || group.rows[0]?.is_container === true
                      ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-500'
                      : 'border-(--color-border) bg-(--color-surface-2) text-(--color-muted)'
                  "
                  @click="
                    updateGroupField(
                      group.name,
                      'is_container',
                      group.rows[0]?.is_container === 'true' || group.rows[0]?.is_container === true
                        ? 'false'
                        : 'true'
                    )
                  "
                >
                  <span
                    class="w-2 h-2 rounded-full shrink-0"
                    :class="
                      group.rows[0]?.is_container === 'true' || group.rows[0]?.is_container === true
                        ? 'bg-emerald-500'
                        : 'bg-(--color-border)'
                    "
                  />
                  <span class="flex flex-col items-start text-left leading-tight">
                    <span>{{ $t('import.isContainer') }}</span>
                    <span class="text-xs font-normal opacity-60">
                      {{ $t('import.containerDesc') }}
                    </span>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </template>

        <!-- Rows (collapsible) -->
        <template v-if="!collapsedGroups.has(group.name)">
          <div
            v-for="row in group.rows"
            :key="row.__idx"
            class="border-t border-(--color-border) bg-(--color-surface) px-4 py-3"
          >
            <p v-if="rowErrorMessages(row).length" class="mb-2 text-red-400 text-xs" role="alert">
              {{ $t('import.rowPreview', { n: row.__idx + 1 }) }}: {{ rowErrorMessages(row).join('; ') }}
            </p>
            <div class="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3 lg:grid-cols-4">
              <!-- Asset Tag -->
              <BaseFormField
                :label="$t('itemView.assetTag')"
                density="compact"
                :error="cellError(row, 'asset_tag') || ''"
              >
                <BaseInput
                  :model-value="row.asset_tag"
                  density="compact"
                  :title="cellError(row, 'asset_tag')"
                  @update:model-value="updateCell(row, 'asset_tag', $event)"
                />
              </BaseFormField>

              <!-- Serial Number -->
              <BaseFormField :label="$t('itemView.serialNumber')" density="compact">
                <BaseInput
                  :model-value="row.serial_number"
                  density="compact"
                  @update:model-value="updateCell(row, 'serial_number', $event)"
                />
              </BaseFormField>

              <!-- Purchase Price -->
              <BaseFormField :label="$t('itemView.purchasePrice')" density="compact">
                <BaseInput
                  :model-value="row.purchase_price"
                  type="number"
                  step="0.01"
                  density="compact"
                  @update:model-value="updateCell(row, 'purchase_price', $event)"
                />
              </BaseFormField>

              <!-- Purchase Date -->
              <BaseFormField :label="$t('ui.purchaseDate')" density="compact">
                <BaseInput
                  :model-value="row.purchase_date"
                  type="date"
                  density="compact"
                  @update:model-value="updateCell(row, 'purchase_date', $event)"
                />
              </BaseFormField>

              <!-- Status -->
              <BaseFormField :label="$t('common.status')" density="compact">
                <BaseSelect
                  :model-value="row.status || '1'"
                  density="compact"
                  @update:model-value="updateCell(row, 'status', $event)"
                >
                  <option v-for="opt in STATUS_OPTIONS" :key="opt.value" :value="opt.value">
                    {{ opt.label }}
                  </option>
                </BaseSelect>
              </BaseFormField>

              <!-- Area -->
              <BaseFormField :label="$t('import.fieldArea')" density="compact">
                <BaseSelect
                  :model-value="row.area"
                  density="compact"
                  @update:model-value="updateCell(row, 'area', $event)"
                >
                  <option value="">{{ $t('common.none') }}</option>
                  <option v-if="isUnlistedArea(row.area)" :value="row.area">{{ row.area }}</option>
                  <option v-for="a in areas" :key="a.id" :value="String(a.id)">{{ a.name }}</option>
                </BaseSelect>
              </BaseFormField>

              <!-- Supplier -->
              <BaseFormField :label="$t('assetValues.groupOptions.supplier')" density="compact">
                <BaseInput
                  :model-value="row.supplier"
                  density="compact"
                  @update:model-value="updateCell(row, 'supplier', $event)"
                />
              </BaseFormField>

              <!-- Available Quantity (only for Quantity/Consumable types) -->
              <BaseFormField
                v-if="[3, 4].includes(getEffectiveType(group.name))"
                :label="$t('ui.availableQty')"
                density="compact"
              >
                <BaseInput
                  :model-value="row.available_quantity"
                  type="number"
                  min="0"
                  density="compact"
                  @update:model-value="updateCell(row, 'available_quantity', $event)"
                />
              </BaseFormField>

              <!-- Comment (full width) -->
              <BaseFormField
                class="col-span-2 sm:col-span-3 lg:col-span-4"
                :label="$t('itemView.comment')"
                density="compact"
              >
                <BaseInput
                  :model-value="row.comment"
                  density="compact"
                  @update:model-value="updateCell(row, 'comment', $event)"
                />
              </BaseFormField>
            </div>
          </div>
        </template>
      </div>
    </template>

    <p v-if="saveError" class="text-red-400 text-sm">{{ saveError }}</p>

    <div class="flex items-center justify-between">
      <p v-if="hasErrors" class="text-red-400 text-sm flex items-center gap-1">
        <MdiAlertCircle class="w-4 h-4" />
        {{ $t('import.previewIssueCount', { count: totalErrors }) }}
      </p>

      <button
        class="btn btn-primary flex items-center gap-2 ml-auto"
        :disabled="hasErrors || !!loadError || saving || loading"
        @click="advance"
      >
        {{ saving ? $t('common.saving') : $t('ui.nextImport') }}
        <MdiChevronRight class="w-4 h-4" />
      </button>
    </div>
  </div>

  <!-- Saving overlay — shown while background pages are loading before advance -->
  <div
    v-if="saving"
    class="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-(--color-bg)/80 backdrop-blur-sm"
  >
    <span
      class="inline-block w-12 h-12 rounded-full border-4 border-(--color-accent) border-t-transparent animate-spin"
    />
    <p class="text-(--color-text) font-medium">{{ $t('ui.savingYourEdits') }}</p>
    <p class="text-(--color-muted) text-sm">{{ $t('ui.loadingRemainingRowsBeforeProceeding') }}</p>
  </div>
</template>
